/**
 * Hartenjagen. The rules, and nothing else.
 *
 * Pure functions over cards, like the rest of `game-logic`. What is reused from
 * boerenbridge is reused literally rather than adapted: `trickWinner(trick,
 * null)` falls back to the highest card of the led suit, which *is* the rule
 * here, and `deal(4, 13)` happens to exhaust the deck exactly so there is no
 * card left to turn for trump. That is not a coincidence worth hiding — it is
 * why a second game was cheap.
 *
 * What is genuinely new lives here: the two of clubs opens, the first trick is
 * safe, hearts must be broken before they may be led, and the scoring — where
 * the whole point is that low is good, except when it is spectacularly not.
 */

import type { Card, Suit, Trick } from '@shared/types/game';
import { cardsEqual, removeCard } from './deck';

/**
 * Two games under one name.
 *
 * `amerikaans` is Hearts, also called Black Lady: 52 cards, every heart a point
 * and the queen of spades thirteen, twenty-six to go round, three cards passed on
 * before every hand and the two of clubs to open.
 *
 * `nederlands` is the game most Dutch families mean: 32 cards, the queen of
 * spades five and the jack of clubs two on top of the hearts, so fifteen a hand,
 * and nothing is passed at all.
 *
 * Both are played to a target with the lowest score winning. The Dutch game is
 * also played with a second, reversed half in some houses — sources disagree on
 * the details, so that is deliberately not in here.
 */
export type HeartsVariant = 'amerikaans' | 'nederlands';

export interface HeartsRules {
  variant: HeartsVariant;
  /** Cards in each hand. */
  cards: number;
  /** The card that has to lead the first trick. */
  openingCard: Card;
  /** What each of these costs the player who takes it. */
  heart: number;
  queenOfSpades: number;
  jackOfClubs: number;
  /** Everything there is to lose in one hand. */
  total: number;
  /** Three cards round the table before play, or nothing at all. */
  passes: boolean;
  /** Nothing that scores may fall on the very first trick. */
  safeFirstTrick: boolean;
}

const AMERICAN: HeartsRules = {
  variant: 'amerikaans',
  cards: 13,
  openingCard: { suit: 'clubs', rank: 2 },
  heart: 1,
  queenOfSpades: 13,
  jackOfClubs: 0,
  total: 26,
  passes: true,
  safeFirstTrick: true,
};

/*
 * The seven of clubs opens, mirroring the two of clubs: it is the lowest club in
 * a 32-card deck. Dutch sources differ here — some tables let whoever sits left
 * of the dealer lead whatever they like — so this is a choice, made to keep one
 * rule and one code path rather than two.
 */
const DUTCH: HeartsRules = {
  variant: 'nederlands',
  cards: 8,
  openingCard: { suit: 'clubs', rank: 7 },
  heart: 1,
  queenOfSpades: 5,
  jackOfClubs: 2,
  total: 15,
  passes: false,
  safeFirstTrick: false,
};

export function heartsRulesFor(variant: HeartsVariant): HeartsRules {
  return variant === 'nederlands' ? DUTCH : AMERICAN;
}

export const DEFAULT_HEARTS_VARIANT: HeartsVariant = 'amerikaans';

export const HEARTS_PLAYERS = 4;
export const HEARTS_CARDS = 13;

/** Every heart is one, the queen of spades is thirteen. 26 in a round. */
export const TOTAL_PENALTY = 26;

export const TWO_OF_CLUBS: Card = { suit: 'clubs', rank: 2 };
export const QUEEN_OF_SPADES: Card = { suit: 'spades', rank: 12 };
/** Two points in the Dutch game, nothing at all in the American one. */
export const JACK_OF_CLUBS: Card = { suit: 'clubs', rank: 11 };

/** How many cards each player passes before the hand starts. */
export const PASS_COUNT = 3;

export type PassDirection = 'left' | 'right' | 'across' | 'none';

/**
 * The passing rotation: left, right, across, then a hand with no passing at all.
 *
 * The fourth is not a gap. Without it every hand would let the player holding
 * the queen of spades simply hand her on, and the deal nobody can escape is
 * what makes the game bite.
 */
const ROTATION: readonly PassDirection[] = ['left', 'right', 'across', 'none'];

export function passDirectionFor(handNo: number): PassDirection {
  // handNo is 1-based, matching roundNo elsewhere.
  return ROTATION[(handNo - 1) % ROTATION.length];
}

/** Who receives the cards this seat passes. */
export function passTarget(seat: number, direction: PassDirection): number | null {
  switch (direction) {
    case 'left':
      return (seat + 1) % HEARTS_PLAYERS;
    case 'right':
      return (seat + HEARTS_PLAYERS - 1) % HEARTS_PLAYERS;
    case 'across':
      return (seat + 2) % HEARTS_PLAYERS;
    case 'none':
      return null;
  }
}

/** The seat holding the two of clubs leads the first trick. */
export function openingLeader(
  hands: readonly (readonly Card[])[],
  rules: HeartsRules = AMERICAN
): number {
  const seat = hands.findIndex((hand) => hand.some((card) => cardsEqual(card, rules.openingCard)));
  if (seat === -1) throw new Error('nobody holds the opening card');
  return seat;
}

export function isPenaltyCard(card: Card, rules: HeartsRules = AMERICAN): boolean {
  return penaltyFor(card, rules) > 0;
}

/** What this one card costs whoever takes it. */
export function penaltyFor(card: Card, rules: HeartsRules): number {
  if (card.suit === 'hearts') return rules.heart;
  if (cardsEqual(card, QUEEN_OF_SPADES)) return rules.queenOfSpades;
  if (cardsEqual(card, JACK_OF_CLUBS)) return rules.jackOfClubs;
  return 0;
}

export interface HeartsPlayContext {
  /** Cards played to the current trick, leader first. */
  trick: Trick;
  /** True until a heart or the queen of spades has been played to any trick. */
  heartsBroken: boolean;
  /** True for the very first trick of the hand. */
  isFirstTrick: boolean;
  /**
   * Which of the two games this table is playing.
   *
   * Required rather than defaulted: a missing variant would quietly hand a Dutch
   * table the American rules, and the difference is exactly the kind that only
   * shows up when somebody is refused a card they should have been allowed.
   */
  rules: HeartsRules;
}

/**
 * Cards that may legally be played.
 *
 * Three restrictions on top of "follow suit", and each exists to stop the same
 * thing: dumping your worst cards before anyone can react.
 */
export function heartsLegalPlays(hand: readonly Card[], context: HeartsPlayContext): Card[] {
  const { trick, heartsBroken, isFirstTrick, rules } = context;

  if (trick.length === 0) {
    // The hand opens with one named card, from whoever holds it: the two of clubs
    // in the American game, the seven in the shorter Dutch deck.
    if (isFirstTrick) {
      const opener = hand.find((card) => cardsEqual(card, rules.openingCard));
      return opener ? [opener] : [...hand];
    }

    if (heartsBroken) return [...hand];

    /*
     * Hearts may not be led until they have been broken — unless that is all
     * there is. Without the escape a player holding nothing but hearts could
     * not move, which is a rule that deadlocks rather than restricts.
     */
    const other = hand.filter((card) => card.suit !== 'hearts');
    return other.length > 0 ? other : [...hand];
  }

  const led = trick[0].card.suit;
  const following = hand.filter((card) => card.suit === led);
  if (following.length > 0) {
    /*
     * Even following suit, the first trick is safe — which matters for exactly
     * one card: a player void in clubs cannot be made to eat the queen of
     * spades on the opening trick.
     */
    if (isFirstTrick && rules.safeFirstTrick) {
      const harmless = following.filter((card) => !isPenaltyCard(card, rules));
      return harmless.length > 0 ? harmless : following;
    }
    return following;
  }

  /*
   * Void in the led suit. Free to discard anything — except on the American
   * game's first trick, where nothing that scores may be thrown. The Dutch game
   * has no such rule, so there the queen can fall straight away.
   */
  if (isFirstTrick && rules.safeFirstTrick) {
    const harmless = hand.filter((card) => !isPenaltyCard(card, rules));
    return harmless.length > 0 ? harmless : [...hand];
  }

  return [...hand];
}

export function isLegalHeartsPlay(
  card: Card,
  hand: readonly Card[],
  context: HeartsPlayContext
): boolean {
  return heartsLegalPlays(hand, context).some((legal) => cardsEqual(legal, card));
}

/** Whether this trick breaks hearts. */
export function breaksHearts(trick: Trick): boolean {
  return trick.some((played) => isPenaltyCard(played.card));
}

/** Raw penalty for a pile of taken cards, before the moon is considered. */
export function penaltyOf(cards: readonly Card[], rules: HeartsRules = AMERICAN): number {
  return cards.reduce((total, card) => total + penaltyFor(card, rules), 0);
}

export interface HeartsHandResult {
  /** Points added to each seat this hand, indexed by seat. */
  points: number[];
  /** The seat that shot the moon, if anyone did. */
  moonSeat: number | null;
}

/**
 * Score a finished hand.
 *
 * Shooting the moon — taking all twenty-six — turns the round inside out: the
 * player who did it scores nothing and everyone else takes the full twenty-six.
 * That is the rule the whole game is played around, so it is asserted directly
 * rather than left to emerge from the arithmetic.
 */
export function scoreHeartsHand(
  taken: readonly (readonly Card[])[],
  rules: HeartsRules = AMERICAN
): HeartsHandResult {
  const raw = taken.map((pile) => penaltyOf(pile, rules));
  const moonSeat = raw.findIndex((points) => points === rules.total);

  if (moonSeat === -1) return { points: raw, moonSeat: null };

  return {
    points: raw.map((_, seat) => (seat === moonSeat ? 0 : rules.total)),
    moonSeat,
  };
}

/**
 * Apply a set of passes, all at once.
 *
 * Simultaneous by construction: every hand is stripped first and only then
 * added to, so a card passed on cannot be passed on again in the same exchange.
 */
export function applyPasses(
  hands: readonly (readonly Card[])[],
  passes: readonly (readonly Card[])[],
  direction: PassDirection
): Card[][] {
  if (direction === 'none') return hands.map((hand) => [...hand]);

  const stripped = hands.map((hand, seat) =>
    passes[seat].reduce((rest, card) => removeCard(rest, card), [...hand])
  );

  for (let seat = 0; seat < hands.length; seat++) {
    const target = passTarget(seat, direction);
    if (target === null) continue;
    stripped[target].push(...passes[seat]);
  }

  return stripped;
}

/** The game ends when someone reaches this, and the *lowest* score wins. */
export const DEFAULT_TARGET_SCORE = 100;

export function heartsWinners(totals: readonly number[]): number[] {
  const lowest = Math.min(...totals);
  return totals.flatMap((total, seat) => (total === lowest ? [seat] : []));
}

/** Sort a hand for display: by suit, then rank. */
export function sortHeartsHand(hand: readonly Card[]): Card[] {
  const order: Record<Suit, number> = { clubs: 0, diamonds: 1, spades: 2, hearts: 3 };
  return [...hand].sort((a, b) => order[a.suit] - order[b.suit] || a.rank - b.rank);
}
